/**
 * WallSketch test-fixture capture.
 *
 * A fixture records the full INPUT a skeleton 3D extrusion depends on (nodes,
 * edges, floor height, metadata, wall regions, and the project floor heights
 * that per-element height constraints resolve against) plus a stable SUMMARY of
 * the resulting extruded geometry to assert against in regression tests
 * (triangle/vertex counts, rounded bounding box). The raw geometry
 * (positions/indices) is included too for exact snapshot tests when wanted.
 *
 * Captured from the layout editor's debug menu; consumed by skeleton extrusion
 * unit tests that rebuild the geometry from `input` and compare against
 * `expected`.
 */

import * as THREE from 'three';
import { SkeletonNodeData, SkeletonEdgeData, WallRegionData } from '@pointorama/potree-shared';
import { FloorHeightInfo } from './resolveSkeletonHeight';
import type { PipelineConfig } from '../BasePipeline';
import type { SkeletonPassKwargs } from '../SkeletonProcessingPipeline';

/** JSON-serializable room/outline polygon (the `__OUTER__` shell + rooms/cavities). */
export interface FixtureRoomConnectivity {
  identifier: string;
  polygon: { x: number; y: number }[];
}

export interface SkeletonFixtureInput {
  nodes: SkeletonNodeData[];
  edges: SkeletonEdgeData[];
  floorHeight: number;
  metadata: string | null;
  wallRegions: WallRegionData[];
  /** Project floor heights — per-element height constraints resolve against these. */
  floorGroups: FloorHeightInfo[];
  /**
   * Room + outline polygons the skeleton was built from. Optional so older
   * fixtures (captured before the unified export) still parse. Needed to re-run
   * the room-aware passes (e.g. centerMatchWallsPass / roomId assignment) and to
   * reproduce wall-skinning from real geometry.
   */
  roomConnectivity?: FixtureRoomConnectivity[];
  /**
   * The skeleton processing pipeline ("skeleton stack") that produced this
   * skeleton — pass list, kwargs, and enabled flags. Optional; present on
   * unified exports for self-contained bug reports.
   */
  pipelineConfig?: PipelineConfig<SkeletonPassKwargs>;
}

export interface SkeletonGeometrySummary {
  triangleCount: number;
  vertexCount: number;
  /** Axis-aligned bounds, rounded to mm, as [minX,minY,minZ, maxX,maxY,maxZ]. */
  bounds: [number, number, number, number, number, number] | null;
}

export interface SkeletonFixture {
  description: string;
  /** Stable geometry invariants for assertions. */
  expected: SkeletonGeometrySummary;
  input: SkeletonFixtureInput;
  /** Raw merged geometry for exact snapshot tests (optional to assert against). */
  rawGeometry: { positions: number[]; indices: number[] | null };
}

/** Round to mm so float jitter doesn't break equality. */
const r3 = (v: number) => Math.round(v * 1000) / 1000;

/**
 * Summarize an array of wall meshes' geometry into stable invariants + a merged
 * raw position/index dump. Triangle count sums across meshes; bounds is the
 * union AABB.
 */
export function summarizeWallGeometry(meshes: THREE.Mesh[]): { summary: SkeletonGeometrySummary; raw: { positions: number[]; indices: number[] | null } } {
  const positions: number[] = [];
  const indices: number[] = [];
  let hasIndex = false;
  let triangleCount = 0;
  const box = new THREE.Box3();
  box.makeEmpty();
  const tmp = new THREE.Vector3();

  for (const mesh of meshes) {
    const geom = mesh.geometry;
    const posAttr = geom.getAttribute('position');
    if (!posAttr) continue;

    const vertexBase = positions.length / 3;
    for (let i = 0; i < posAttr.count; i++) {
      tmp.fromBufferAttribute(posAttr, i);
      positions.push(tmp.x, tmp.y, tmp.z);
      box.expandByPoint(tmp);
    }

    const idx = geom.getIndex();
    if (idx) {
      hasIndex = true;
      for (let i = 0; i < idx.count; i++) indices.push(idx.getX(i) + vertexBase);
      triangleCount += idx.count / 3;
    } else {
      triangleCount += posAttr.count / 3;
    }
  }

  const vertexCount = positions.length / 3;
  const bounds: SkeletonGeometrySummary['bounds'] = box.isEmpty() ? null : [r3(box.min.x), r3(box.min.y), r3(box.min.z), r3(box.max.x), r3(box.max.y), r3(box.max.z)];

  return {
    summary: { triangleCount, vertexCount, bounds },
    raw: { positions, indices: hasIndex ? indices : null },
  };
}

/**
 * Parse a fixture JSON and load its nodes/edges onto a wall sketch — in
 * memory only, nothing is persisted. Shared by the wall-create and
 * wall-sketch-editor debug menus' "Load WallSketch Fixture" buttons.
 * Returns false (with a console.warn) on malformed input.
 */
export function applyFixtureJsonToWallSketch(sketch: { setData(nodes: SkeletonNodeData[], edges: SkeletonEdgeData[], metadata: string | null): void }, json: string): boolean {
  let fixture: SkeletonFixture;
  try {
    fixture = JSON.parse(json) as SkeletonFixture;
  } catch (e) {
    console.warn('Load skeleton fixture: invalid JSON', e);
    return false;
  }
  const input = fixture.input;
  if (!input?.nodes || !input?.edges) {
    console.warn('Load skeleton fixture: JSON has no input.nodes/edges');
    return false;
  }
  // Fixture JSON stores positions as plain {x,y,z}; rehydrate to Vector3 so
  // the skeleton geometry code (which calls Vector3 methods) works.
  const nodes: SkeletonNodeData[] = input.nodes.map((n) => ({
    ...n,
    position: new THREE.Vector3(n.position.x, n.position.y, n.position.z),
  }));
  sketch.setData(nodes, input.edges, input.metadata);
  return true;
}

/** Trigger a browser download of `fixture` as a pretty-printed JSON file. */
export function downloadSkeletonFixture(fixture: SkeletonFixture, filename: string): void {
  const json = JSON.stringify(fixture, null, 2);
  const blob = new Blob([json], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}
